import {
  AnchoredPanelComponent,
  BookmarkablePage,
  FormattedString,
  FullPagePanel,
  MessageType,
  PanelMessage,
  useNavigation,
} from "o4a-react";
import * as React from "react";
import { useLocation } from "react-router-dom";
import { Stack, Text } from "@fluentui/react";
import * as Messages from "../../codegen/Messages";
import { ConsoleContext } from "../../console/ConsoleContext";
import { FilterState, FilterStateContext } from "../../console/FilterContext";
import { Settings, SettingsContext } from "../../console/SettingsContext";
import { useSubscriptions } from "../../hooks/useSubscriptions";
import { PageId, PageRegistrationConfig, SUPPORT_ROUTE } from "../../constants/pluginConstants";
import { SvgIconIds } from "../../constants/uiConstants";
import { RoleBasedAction } from "../../helpers/roleHelper";
import { useRoles } from "../../hooks/useRoles";
import { SupportList } from "./SupportList";
import { ociSupportLink, ociHelpLink } from "../../constants/docConstants";

const registrationIds = PageRegistrationConfig[PageId.SUPPORT].map(config => config.key);

export const SupportListPage = (): JSX.Element => {
  const { back, navigateToSelf } = useNavigation(ConsoleContext);
  const { pathname } = useLocation();
  const filterState = React.useContext<FilterState>(FilterStateContext);

  const [panelRef, setPanelRef] = React.useState<AnchoredPanelComponent>({} as AnchoredPanelComponent);

  const { isActionAllowed, actionRequiredRoles } = useRoles();
  const disableList = !isActionAllowed(RoleBasedAction.LIST_SUPPORT);
  const requiredRole = actionRequiredRoles(RoleBasedAction.LIST_SUPPORT)[0]?.displayName;

  React.useEffect(() => {
    // In case page was navigated to directly by entring its URL in the browser
    navigateToSelf(SUPPORT_ROUTE, registrationIds[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (filterState.pillFilterStateValue.listId !== pathname) {
      filterState.setFilterStateValue({ filterValues: undefined, listId: undefined, defaultPills: undefined });
      filterState.setFilterTextValue(undefined);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get the preferred subscription info
  const { subscriptions } = useSubscriptions();
  const { preferredSubscription } = React.useContext<Settings>(SettingsContext);
  const preferredSubscriptionInfo = subscriptions?.find(sub => sub.id === preferredSubscription);

  const tenancyName: string | undefined = preferredSubscriptionInfo?.ociTenancyName;
  const domainName: string | undefined = preferredSubscriptionInfo?.ociDomainName;

  return (
    <BookmarkablePage
      appContext={ConsoleContext}
      registrationIds={registrationIds}
      title={Messages.listSupport.titles.short()}
    >
      <FullPagePanel
        componentRef={setPanelRef}
        title={Messages.listSupport.titles.long()}
        icon={SvgIconIds.supportSvg}
        onClose={back}
        isOpen
      >
        <Stack style={{ maxWidth: "70%", width: "70%" }}>
          <FormattedString inputText={Messages.listSupport.ociSupportPortal.description(ociSupportLink, ociHelpLink)} />
        </Stack>

        <Stack style={{ maxWidth: "70%", width: "70%", marginTop: 16 }}>
          <Text variant="mediumPlus" styles={{ root: { fontWeight: 600, marginBottom: 8 } }}>{Messages.listSupport.ociSupportDetails.ociTenancyDetails()}</Text>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            <Text variant="smallPlus" styles={{ root: { fontWeight: 600, width: 100 } }}>{Messages.listSupport.ociSupportDetails.tenancyName()}</Text>
            <Text variant="smallPlus">{tenancyName || "-"}</Text>
          </Stack>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }} styles={{ root: { marginTop: 4 } }}>
            <Text variant="smallPlus" styles={{ root: { fontWeight: 600, width: 100 } }}>{Messages.listSupport.ociSupportDetails.domainName()}</Text>
            <Text variant="smallPlus">{domainName || "-"}</Text>
          </Stack>
        </Stack>
      </FullPagePanel>
    </BookmarkablePage>
  );
};
